/**
 * Shell snippets shown on `/tutorial`, defined once so a command can never drift between the
 * Indonesian and the English page. Every command here is pinned to the real command tree in
 * `src/lib/api-schema.server.ts` (CLI_COMMANDS), which `api-schema.test.ts` keeps honest.
 */

export const CMD = {
  installAgentClaude: ['npm install -g @anthropic-ai/claude-code', 'claude'],
  installAgentCodex: ['npm install -g @openai/codex', 'codex'],
  installCli: ['npm install -g https://bystep.dev/downloads/bystep-cli-0.7.0.tgz', 'bystep --version'],
  help: ['bystep --llms', 'bystep task --llms'],
  connect: ['bystep login', 'bystep connect --workspace <workspaceId> --plan <planId>', 'bystep init'],
  doctor: ['bystep doctor', 'bystep doctor --repo'],
  loop: [
    'bystep task next --plan <planId> --json',
    'bystep task start <ref>',
    '# … agen mengerjakan task ini saja …',
    'bystep task verify <ref>',
    'bystep task complete <ref>',
  ],
  loopEn: [
    'bystep task next --plan <planId> --json',
    'bystep task start <ref>',
    '# … the agent works on this one task …',
    'bystep task verify <ref>',
    'bystep task complete <ref>',
  ],
  fail: ['bystep task fail <ref> "<reason>"'],
  approve: ['bystep task approve <phase> --layer frontend', 'bystep task approve <phase> --layer backend'],
  approveReturn: ['bystep task approve <phase> --layer frontend --return <ref>:"<reason>"'],
  sync: ['bystep sync --plan <planId>', 'bystep status'],
  importSpec: ['bystep import ./spec --from speckit --workspace <workspaceId>'],
  planExec: [
    'bystep plan exec --plan <planId> --mode checkpoint --verifier local',
    'bystep plan exec --plan <planId> --mode strict',
  ],
  wave: ['bystep wave next --plan <planId> --json'],
  serveFirst: ['bystep serve --doctor', 'bystep login', 'bystep serve'],
  servePair: ['bystep serve --pair <8-char-code>'],
  serveOptions: [
    'bystep serve --parallel 2',
    'bystep serve --runs-per-hour 6',
    'bystep serve --name "MacBook kantor"',
  ],
  service: ['bystep service install', 'bystep service status', 'bystep service uninstall'],
  update: ['bystep update', 'bystep update --apply', 'bystep update --apply --channel preview'],
  customAgent: [
    '# .bystep/agents/punyaku.sh  (Windows: punyaku.ps1)',
    '#!/usr/bin/env bash',
    'echo "$BYSTEP_PROMPT" | my-own-agent --run',
    '',
    '# pilih skrip itu saat menjalankan tim agent:',
    'bystep workflow start --plan <planId> --agent custom:punyaku',
  ],
  customAgentEn: [
    '# .bystep/agents/mine.sh  (Windows: mine.ps1)',
    '#!/usr/bin/env bash',
    'echo "$BYSTEP_PROMPT" | my-own-agent --run',
    '',
    '# select that script when you start an agent team:',
    'bystep workflow start --plan <planId> --agent custom:mine',
  ],
  workflow: [
    'bystep workflow start --plan <planId> --parallel 2 --usd 5 --minutes 60',
    'bystep workflow list --plan <planId>',
    'bystep workflow show <id>',
    'bystep workflow message <id> "<pesan untuk tim>"',
    'bystep workflow stop <id>',
  ],
  workflowEn: [
    'bystep workflow start --plan <planId> --parallel 2 --usd 5 --minutes 60',
    'bystep workflow list --plan <planId>',
    'bystep workflow show <id>',
    'bystep workflow message <id> "<guidance for the team>"',
    'bystep workflow stop <id>',
  ],
  decision: [
    'bystep decision list',
    'bystep decision add "<keputusan>" --why "<alasan>"',
    'bystep decision replace <id> "<keputusan baru>"',
  ],
  decisionEn: [
    'bystep decision list',
    'bystep decision add "<decision>" --why "<reason>"',
    'bystep decision replace <id> "<new decision>"',
  ],
  report: ['bystep report --state blocked --message "<apa yang menghambat>"', 'bystep review submit --body \'{"verdict":"…","comments":[]}\''],
  reportEn: ['bystep report --state blocked --message "<what is blocking>"', 'bystep review submit --body \'{"verdict":"…","comments":[]}\''],
  teamConnect: ['bystep login', 'bystep connect --workspace <workspaceId> --plan <planId>', 'bystep team tasks --mine'],
  teamClaim: ['bystep team claim <ref> --agent codex', 'bystep team context <ref>'],
  teamVerify: ['bystep team heartbeat <ref>', 'bystep task verify <ref>', 'bystep team context <ref>'],
  teamReportJson: [
    '{',
    '  "status": "ready_for_review",',
    '  "acceptanceHash": "<current-acceptance-hash>",',
    '  "commitHash": "<full-commit-sha>",',
    '  "summary": "<summary-of-work-and-checks>",',
    '  "evidenceIds": ["<verification-evidence-id>"]',
    '}',
  ],
  teamReport: ['bystep team report <ref> --file report.json'],
  teamResourceClaim: ['bystep team claim <ref> --agent codex --file resources.json', 'bystep team context <ref>'],
  teamHandoff: ['bystep team handoff <ref> --file handoff.json'],
  teamRelinquish: ['bystep team relinquish <ref> --file reason.json'],
  installMcp: ['npm install -g https://bystep.dev/downloads/bystep-mcp-0.5.0.tgz'],
  mcpStdio: [
    '{',
    '  "mcpServers": {',
    '    "bystep": {',
    '      "command": "bystep-mcp",',
    '      "env": {',
    '        "BYSTEP_REPO": "/absolute/path/to/repo",',
    '        "BYSTEP_REPO_ROOT": "/absolute/path/to/repo"',
    '      }',
    '    }',
    '  }',
    '}',
  ],
  mcpClaudeAdd: ['claude mcp add bystep -- bystep-mcp'],
  mcpCodex: [
    '# ~/.codex/config.toml',
    '[mcp_servers.bystep]',
    'command = "bystep-mcp"',
    '[mcp_servers.bystep.env]',
    'BYSTEP_REPO = "/absolute/path/to/repo"',
    'BYSTEP_REPO_ROOT = "/absolute/path/to/repo"',
  ],
  mcpHosted: ['https://bystep.dev/api/mcp', 'Authorization: Bearer bs_…'],
} as const
